
import React from 'react';
import Layout from '@/components/Layout/Layout';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PregnancyWeightCalculator from '@/components/SpecialCalculators/PregnancyCalculators/PregnancyWeightCalculator';

const PregnancyWeightCalculatorPage = () => {
  return (
    <Layout>
      <div className="container mx-auto max-w-3xl px-4">
        <div className="mb-6">
          <Link to="/special" className="flex items-center text-primary hover:underline mb-2">
            <ArrowLeft size={16} className="mr-1" />
            Back to Special Calculators
          </Link>
          <h1 className="text-3xl font-bold mb-2">Pregnancy Weight Gain Calculator</h1>
          <p className="text-gray-600 mb-6">
            Calculate recommended weight gain during pregnancy based on your pre-pregnancy BMI.
          </p>
        </div>
        
        <div className="mb-8">
          <PregnancyWeightCalculator />
        </div>
        
        <div className="mt-10 prose max-w-none">
          <h2 className="text-xl font-semibold mb-4">About Pregnancy Weight Gain</h2>
          <p>
            Healthy weight gain during pregnancy is important for the development of your baby.
            The amount of weight you should gain depends on your pre-pregnancy BMI (Body Mass Index),
            with different recommendations for underweight, normal weight, overweight, and obese women.
          </p>
          
          <h3 className="text-lg font-medium mt-6 mb-2">Recommended Weight Gain by BMI Category</h3>
          <table className="min-w-full border border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left">Pre-pregnancy BMI</th>
                <th className="border p-2 text-left">BMI Category</th>
                <th className="border p-2 text-left">Recommended Weight Gain (kg)</th>
                <th className="border p-2 text-left">Recommended Weight Gain (lbs)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border p-2">Less than 18.5</td>
                <td className="border p-2">Underweight</td>
                <td className="border p-2">12.5 - 18 kg</td>
                <td className="border p-2">28 - 40 lbs</td>
              </tr>
              <tr>
                <td className="border p-2">18.5 - 24.9</td>
                <td className="border p-2">Normal Weight</td>
                <td className="border p-2">11.5 - 16 kg</td>
                <td className="border p-2">25 - 35 lbs</td>
              </tr>
              <tr>
                <td className="border p-2">25 - 29.9</td>
                <td className="border p-2">Overweight</td>
                <td className="border p-2">7 - 11.5 kg</td>
                <td className="border p-2">15 - 25 lbs</td>
              </tr>
              <tr>
                <td className="border p-2">30 or more</td>
                <td className="border p-2">Obese</td>
                <td className="border p-2">5 - 9 kg</td>
                <td className="border p-2">11 - 20 lbs</td>
              </tr>
            </tbody>
          </table>
          
          <h3 className="text-lg font-medium mt-6 mb-2">Pattern of Weight Gain During Pregnancy</h3>
          <p>
            Weight gain isn't usually distributed evenly throughout pregnancy. A typical pattern might be:
          </p>
          <ul className="list-disc ml-6">
            <li><strong>First trimester:</strong> 1-2 kg (2-4 lbs)</li>
            <li><strong>Second trimester:</strong> 5-6 kg (11-13 lbs)</li>
            <li><strong>Third trimester:</strong> 5-6 kg (11-13 lbs)</li>
          </ul>
          
          <div className="bg-yellow-50 p-4 rounded-md mt-6">
            <h3 className="text-lg font-medium text-yellow-800 mb-2">Important Note</h3>
            <p className="text-yellow-800">
              This calculator provides general guidelines. Always consult with your healthcare provider
              for personalized recommendations about healthy weight gain during pregnancy.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PregnancyWeightCalculatorPage;
